import React, { useEffect, useState } from "react";
import api from "../api";
import { useLocation, useHistory } from "react-router-dom";
import LottieAnimation from "../LottieAnimation";
import moment from "moment";
import { useAmazonContext } from "../../Contexts/AmazonContext";

const ReactNativeLoginHandling = ({ setIsLoggedIn, setTokenExpiration }) => {
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const { userRole, setUserRole, setShowUpdateModal, setUpdateInfo, fetchUserStreak, fetchUserLessons } =
    useAmazonContext();

  // Function to handle login
  const handleLogin = () => {
    // Perform login logic
    setIsLoggedIn(true);

    // Set expiration time to infinity
    const expirationTime = new Date("9999-12-31T23:59:59");
    setTokenExpiration(expirationTime);

    // Store isLoggedIn and expirationTime in localStorage
    localStorage.setItem("isLoggedIn", true);
    localStorage.setItem("tokenExpiration", expirationTime.getTime());

    // Check if there's a redirect URL in the query params
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get("redirect");

    if (redirectUrl) {
      history.push(redirectUrl);
    } else {
      history.push("/home");
    }
  };

  // Function to process the idToken
  const processIdToken = async (idToken) => {
    try {
      console.log("Processing ID token:", idToken);
      // Use the id token to authenticate the user by verifying it with your backend
      const res = await api.post("/auth/verify-token", { idToken });
      const userObject = res.data;

      const response = await api.post("/updateUser", {
        userEmail: userObject.email,
        givenName: userObject.given_name,
        familyName: userObject.family_name,
        isLogin: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Add user's timezone
      });

      const user = response?.data;
      localStorage.setItem("userEmail", userObject.email);
      localStorage.setItem(
        "userGivenName",
        user?.displayGivenName || user?.givenName || userObject.given_name
      );
      localStorage.setItem(
        "userFamilyName",
        user?.displayFamilyName || user?.familyName || userObject.family_name
      );

      handleLogin();
      console.log(userObject);

      // Additional steps from CustomButton
      const formattedDate = moment().format("DD-MM-YYYY hh:mm:ss");
      console.log(formattedDate);

      const requestData = {
        userEmail: userObject.email,
        userFamilyName: userObject.family_name,
        userGivenName: userObject.given_name,
        userPicture: userObject.picture,
        loginDateTime: formattedDate,
      };
      console.log(requestData);

      api
        .post("/insertLoginRecord", requestData)
        .then((response) => {
          if (response.status === 200) {
            return response.data;
          }
          throw new Error("Network response was not ok.");
        })
        .then((data) => {
          console.log(data);
        })
        .catch((error) => {
          if (
            error instanceof SyntaxError &&
            error.message.includes("Unexpected token 'U'")
          ) {
            return;
          }
          console.error("Error from fetch:", error);
        });

      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        console.error("User email not found in local storage");
        return;
      }

      const fetchUserRole = async () => {
        try {
          const response = await api.get("/fetchUserRole", {
            params: {
              userEmail: userEmail,
            },
          });
          setUserRole(response.data.role);
          localStorage.setItem("userRole", response.data.role);
          console.log(localStorage.getItem("userRole"));
        } catch (error) {
          console.error("Error fetching user role:", error);
          setUserRole("user");
          localStorage.setItem("userRole", "user");
          console.log(localStorage.getItem("userRole"));
        }
      };

      // Add update modal check after login
      try {
        const updateResponse = await api.get("/checkUpdateStatus", {
          params: { userEmail: userObject.email },
        });

        if (updateResponse.data.showUpdate) {
          setShowUpdateModal(true);
          setUpdateInfo(updateResponse.data);
        }
      } catch (error) {
        console.error("Error checking for updates:", error);
      }

      fetchUserRole();

      // Fetch user streak and lesson counts immediately after login
      fetchUserStreak();
      fetchUserLessons();
    } catch (error) {
      console.error("Error authenticating with ID token:", error);
      history.replace("/something-went-wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("ReactNativeLoginHandling mounted");

    // 🔥 STEP 1: Try to get token from injection (FAST PATH)
    const injectedToken = window.ReactNativeAuth?.token;

    if (injectedToken) {
      console.log("✅ Token found via injection (fast path):", injectedToken);
      processIdToken(injectedToken);
      return; // Early exit - we have the token!
    }

    console.log("⚠️ No injected token found, setting up postMessage listener (fallback)");

    // 🔥 STEP 2: Set up postMessage listener as fallback (SLOW PATH)
    const handleTokenMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "idToken") {
          console.log("✅ Received idToken from React Native via postMessage:", data.token);
          processIdToken(data.token);
        }
      } catch (err) {
        console.error("Error handling postMessage:", err);
      }
    };

    // Listen for both iOS and Android
    window.addEventListener("message", handleTokenMessage);
    document.addEventListener("message", handleTokenMessage);

    // 🔥 STEP 3: Request token from React Native (in case injection failed)
    if (window.ReactNativeWebView) {
      console.log("📤 Requesting auth token from React Native");
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'REQUEST_AUTH'
      }));
    }

    // 🔥 STEP 4: Safety timeout
    const timeout = setTimeout(() => {
      console.log("⏰ No idToken received within timeout period");
      console.log("This might indicate:");
      console.log("1. Not running in React Native WebView");
      console.log("2. Token injection failed");
      console.log("3. postMessage communication failed");
      setIsLoading(false);
    }, 10000); // 10 second timeout

    // Cleanup function
    return () => {
      window.removeEventListener("message", handleTokenMessage);
      document.removeEventListener("message", handleTokenMessage);
      clearTimeout(timeout);
    };
  }, []);

  if (isLoading) {
    return (
      <div>
        <LottieAnimation
          lottieFile="https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/LottieFiles/loading.json"
          loop={true}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div>Loading...</div>
    </div>
  );
};

export default ReactNativeLoginHandling;
